import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Alert, AlertDescription, AlertTitle, Button, FormField, Input } from '@bookaburra/ui';
import { XCircle } from 'lucide-react';
import { authClient } from '../auth/client';
import { AppHeader } from '../components/AppHeader';
import { useDocumentTitle } from '../lib/use-document-title';

interface SignInSearch {
  redirect?: string;
}

export const Route = createFileRoute('/signin')({
  validateSearch: (search: Record<string, unknown>): SignInSearch => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  component: SignInPage,
});

function SignInPage() {
  useDocumentTitle('Sign in');
  const navigate = useNavigate();
  const { redirect } = useSearch({ from: '/signin' });
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If the user is already signed in, send them on. Honour ?redirect=…
  // when it's a safe same-origin path, otherwise fall back to /.
  useEffect(() => {
    if (sessionPending || !session?.user) return;
    const safe =
      typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//')
        ? redirect
        : '/';
    navigate({ to: safe });
  }, [sessionPending, session, redirect, navigate]);

  // Avoid a form-flash before the redirect runs.
  if (sessionPending || session?.user) {
    return <div className="min-h-dvh bg-background" aria-busy="true" />;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const result = await authClient.signIn.email({
      email: email.trim(),
      password,
    });
    setSubmitting(false);
    if (result.error) {
      setError(result.error.message ?? 'Sign-in failed. Check your email and password.');
      return;
    }
    navigate({ to: redirect || '/' });
  }

  return (
    <div className="min-h-dvh bg-background">
      <AppHeader />
      <main className="mx-auto flex max-w-md flex-col px-6 py-12 sm:py-16">
        <header className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight">Sign in</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            New here?{' '}
            <Link to="/signup" className="font-medium text-primary underline-offset-4 hover:underline">
              Create an account
            </Link>
            .
          </p>
        </header>

      <form onSubmit={onSubmit} className="space-y-5 rounded-2xl border border-brand-sage/15 bg-card p-8 shadow-brand">
        <FormField label="Email" required>
          {({ id, invalid, describedBy }) => (
            <Input
              id={id}
              type="email"
              autoComplete="email"
              required
              invalid={invalid}
              aria-describedby={describedBy}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="sarah@example.com.au"
            />
          )}
        </FormField>
        <FormField label="Password" required>
          {({ id, invalid, describedBy }) => (
            <Input
              id={id}
              type="password"
              autoComplete="current-password"
              required
              invalid={invalid}
              aria-describedby={describedBy}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </FormField>

        {error ? (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertTitle>Couldn&rsquo;t sign you in</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <Button type="submit" className="w-full" loading={submitting} disabled={!email || !password}>
          Sign in
        </Button>
      </form>
      </main>
    </div>
  );
}
